/**
 * Webhooks data hooks — endpoint CRUD, secret rotation (returned once),
 * and the signed test-send.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type { Webhook, WebhookListResponse, WebhookWithSecret } from '../../lib/types.js';

const invalidate = (queryClient: QueryClient, tenantId: string): void => {
  void queryClient.invalidateQueries({ queryKey: queryKeys.webhooks(tenantId) });
};

interface WebhooksListLogic {
  isLoading: boolean;
  data: Webhook[] | undefined;
  error: Error | null;
}

export const useWebhooksListLogic = (tenantId: string | undefined): WebhooksListLogic => {
  const query = useQuery<WebhookListResponse>({
    queryKey: queryKeys.webhooks(tenantId ?? ''),
    queryFn: () => apiRequest<WebhookListResponse>(`/api/webhooks`),
    enabled: Boolean(tenantId),
  });
  return { isLoading: query.isLoading, data: query.data?.webhooks, error: query.error };
};

export const isValidWebhookLabel = (label: string): boolean => /^[a-z0-9-]{3,60}$/.test(label);

/**
 * Names are slugs, so make them slugs AS the person types instead of letting
 * "Fix-test" silently disable the submit button — the constraint becomes
 * invisible rather than a puzzle. Same move the trigger picker makes with
 * custom.<slug>. Length is NOT clamped here: a too-short name still shows as
 * what they typed, with the rule under the field saying why it can't submit.
 */
export const slugifyWebhookLabel = (raw: string): string =>
  raw
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .slice(0, 60);

interface CreateWebhookLogic {
  label: string;
  setLabel: (v: string) => void;
  url: string;
  setUrl: (v: string) => void;
  isSubmitting: boolean;
  error: Error | null;
  submit: () => Promise<WebhookWithSecret>;
  reset: () => void;
}

export const useCreateWebhookLogic = (
  tenantId: string | undefined,
  onSuccess: (webhook: WebhookWithSecret) => void,
): CreateWebhookLogic => {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');

  const mutation = useMutation<WebhookWithSecret, Error, void>({
    mutationFn: () =>
      apiRequest<WebhookWithSecret>(`/api/webhooks`, {
        method: 'POST',
        body: { label: label.trim(), url: url.trim() },
      }),
    onSuccess: (webhook) => {
      invalidate(queryClient, tenantId ?? '');
      onSuccess(webhook);
    },
  });

  return {
    label,
    setLabel,
    url,
    setUrl,
    isSubmitting: mutation.isPending,
    error: mutation.error,
    submit: () => mutation.mutateAsync(),
    reset: () => {
      setLabel('');
      setUrl('');
      mutation.reset();
    },
  };
};

interface WebhookActionsLogic {
  isSubmitting: boolean;
  error: Error | null;
  rotateSecret: (webhookId: string) => Promise<WebhookWithSecret>;
  setEnabled: (webhookId: string, isEnabled: boolean) => Promise<void>;
  remove: (webhookId: string) => Promise<void>;
  sendTest: (webhookId: string) => Promise<{ url: string; status: number }>;
}

export const useWebhookActionsLogic = (tenantId: string | undefined): WebhookActionsLogic => {
  const queryClient = useQueryClient();
  const done = () => invalidate(queryClient, tenantId ?? '');

  const rotate = useMutation<WebhookWithSecret, Error, string>({
    mutationFn: (webhookId) =>
      apiRequest<WebhookWithSecret>(`/api/webhooks/${webhookId}/rotate-secret`, { method: 'POST' }),
    onSuccess: done,
  });
  const enabled = useMutation<unknown, Error, { webhookId: string; isEnabled: boolean }>({
    mutationFn: ({ webhookId, isEnabled }) =>
      apiRequest(`/api/webhooks/${webhookId}/enabled`, {
        method: 'PATCH',
        body: { isEnabled },
      }),
    onSuccess: done,
  });
  const removal = useMutation<unknown, Error, string>({
    mutationFn: (webhookId) => apiRequest(`/api/webhooks/${webhookId}`, { method: 'DELETE' }),
    onSuccess: done,
  });
  const test = useMutation<{ url: string; status: number }, Error, string>({
    mutationFn: (webhookId) =>
      apiRequest<{ url: string; status: number }>(`/api/webhooks/${webhookId}/test`, {
        method: 'POST',
      }),
  });

  return {
    isSubmitting: rotate.isPending || enabled.isPending || removal.isPending || test.isPending,
    // Test failures surface inline on the card's result line, never the banner.
    error: rotate.error ?? enabled.error ?? removal.error,
    rotateSecret: (webhookId) => rotate.mutateAsync(webhookId),
    setEnabled: (webhookId, isEnabled) =>
      enabled.mutateAsync({ webhookId, isEnabled }).then(() => undefined),
    remove: (webhookId) => removal.mutateAsync(webhookId).then(() => undefined),
    sendTest: (webhookId) => test.mutateAsync(webhookId),
  };
};
